import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import useProjects from "hooks/projects/useProjects";
import { useAuth } from "contexts/AuthContext";
import ProjectFormDrawer from "../components/ProjectFormDrawer";
import paths from "routes/paths";

const ProjectFormPage = () => {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const { projects, createProject, updateProject } = useProjects();
  const { user } = useAuth();

  const isEditMode = useMemo(() => Boolean(projectId), [projectId]);
  const [loadingProject, setLoadingProject] = useState(isEditMode);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [project, setProject] = useState(null);

  useEffect(() => {
    if (!isEditMode) {
      setLoadingProject(false);
      return;
    }

    const found = (projects || []).find((item) => String(item.id) === String(projectId));
    setProject(found || null);
    setLoadingProject(false);
  }, [isEditMode, projectId, projects]);

  const handleSubmit = async (values) => {
    setIsSubmitting(true);

    try {
      const payload = {
        code: String(values.code || "").trim().toUpperCase(),
        name: String(values.name || "").trim(),
        description: values.description || null,
      };

      if (isEditMode) {
        if (!project?.id) {
          throw new Error("Project ID is required");
        }

        await updateProject(project.id, payload);
        toast.success("Project updated successfully");
        navigate(paths.projectDetails(projectId));
        return;
      }

      await createProject({
        ...payload,
        creatorUserId: user?.id || null,
      });

      toast.success("Project created successfully");
      navigate(paths.projects);
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        `Failed to ${isEditMode ? "update" : "create"} project`;
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loadingProject) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "400px" }}
      >
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <ProjectFormDrawer
      open
      onClose={() => navigate(isEditMode ? -1 : paths.projects)}
      onSubmit={handleSubmit}
      mode={isEditMode ? "edit" : "create"}
      initialValues={
        project
          ? {
              id: project.id,
              code: project.code || "",
              name: project.name || "",
              description: project.description || "",
              ownerName: project.ownerName || "",
              targetPoints: project.targetPoints || "",
              tags: project.tags || [],
            }
          : null
      }
      loading={isSubmitting}
    />
  );
};

export default ProjectFormPage;
